import { DEFAULT_FILTER } from './user';
import { AppUser } from 'Src/views/App/App';
import axios from 'Src/helpers/api/axiosInstance';
import { DEFAULT_LIMIT, DEFAULT_SKIP } from 'Src/consts/table';
import { Count } from '../../../models/count';

export type GeneralMessageRecipientType = 'students' | 'staffs' | 'governors';

export interface GeneralMessageRecipientsFilter {
  where?: any;
  skip?: number;
  limit?: number;
  order?: any;
}

export interface GeneralMessageRecipientPreviewItem {
  id: string;
  userId: string;
  permissionId: string;
  permissionIds?: string[];
  type: GeneralMessageRecipientType;
  sportIds?: string[];
  sports?: Array<{ id?: string; _id?: string; name?: string }>;
  hasAllSportsAccess?: boolean;
  firstName?: string;
  lastName?: string;
  gender?: string;
  birthday?: string;
  preset?: string;
  presets?: string[];
  schoolId?: string;
  formId?: string;
  formIds?: string[];
  form?: any;
  forms?: any[];
  houseId?: string;
  houseIds?: string[];
  house?: any;
  houses?: any[];
  yearGroup?: number;
  specialEducationNeeds?: boolean;
  pupilsPremium?: boolean;
  freeSchoolMeals?: boolean;
  tagIds?: string[];
  tags?: any[];
}

export interface GeneralMessageRecipientSelectionItem {
  userId: string;
  permissionId: string;
}

export interface GeneralMessageRecipientSelectionGroup {
  mode?: 'MANUAL' | 'FILTER';
  all?: boolean;
  selected?: GeneralMessageRecipientSelectionItem[];
  excluded?: GeneralMessageRecipientSelectionItem[];
  filter?: GeneralMessageRecipientsFilter;
  filters?: GeneralMessageRecipientsFilter[];
}

export interface GeneralMessageRecipientSelection {
  students?: GeneralMessageRecipientSelectionGroup;
  staffs?: GeneralMessageRecipientSelectionGroup;
  governors?: GeneralMessageRecipientSelectionGroup;
}

export interface GeneralMessageRecipientsResolveParams {
  recipientSelection: GeneralMessageRecipientSelection;
  subscribers?: any;
  includeList?: boolean;
  enabledChannels?: {
    email: boolean;
    push: boolean;
    sms: boolean;
  };
  hasChildVariables?: boolean;
}

export interface GeneralMessageRecipientChannelSummary {
  total: number;
  email: number;
  push: number;
  sms: number;
  noChannels: number;
}

export interface GeneralMessageRecipientsResolveResult {
  count: number;
  channelSummary?: GeneralMessageRecipientChannelSummary;
  affectedUserList?: any[];
}

export interface GeneralMessageRecipientPlanPreview extends GeneralMessageRecipientsResolveResult {
  status: 'DRAFT' | 'SCHEDULED';
  source: 'CURRENT_SELECTION' | 'SCHEDULED_SNAPSHOT';
  enabledChannels: Array<'email' | 'sms' | 'push'>;
  message: any;
}

export interface GeneralMessageSelectedRecipientsParams {
  recipientSelection: GeneralMessageRecipientSelection;
  filter?: GeneralMessageRecipientsFilter;
}

export interface GeneralMessageRecipientSearchResult {
  items: GeneralMessageRecipientPreviewItem[];
  count: number;
}

function getGeneralMessageRecipientsUrl(
  user: AppUser,
  recipientType?: GeneralMessageRecipientType,
  action?: string
): string {
  const { activeSchoolId } = user;
  const baseUrl = `${window.apiBase}/i/schools/${activeSchoolId}/messages/general/recipients`;

  if (typeof recipientType !== 'undefined' && typeof action !== 'undefined') {
    return `${baseUrl}/${recipientType}/${action}`;
  }

  if (typeof action !== 'undefined') {
    return `${baseUrl}/${action}`;
  }

  return baseUrl;
}

export function getGeneralMessageRecipientsPreview(
  user: AppUser,
  recipientType: GeneralMessageRecipientType,
  filter: GeneralMessageRecipientsFilter = {}
): Promise<GeneralMessageRecipientPreviewItem[]> {
  const { roleSessionKey: key } = user;
  const config = {
    headers: { usid: key }
  };

  return axios
    .post(getGeneralMessageRecipientsUrl(user, recipientType, 'preview'), filter, config)
    .then((response: { data: GeneralMessageRecipientPreviewItem[] }) => {
      return response.data;
    });
}

export function getGeneralMessageRecipientsCount(
  user: AppUser,
  recipientType: GeneralMessageRecipientType,
  filter: GeneralMessageRecipientsFilter = {}
): Promise<Count> {
  const { roleSessionKey: key } = user;
  const config = {
    headers: { usid: key }
  };

  return axios
    .post(getGeneralMessageRecipientsUrl(user, recipientType, 'count'), filter, config)
    .then((response: { data: Count }) => {
      return response.data;
    });
}

export function searchGeneralMessageRecipients(
  user: AppUser,
  recipientType: GeneralMessageRecipientType,
  filter: GeneralMessageRecipientsFilter = {}
): Promise<GeneralMessageRecipientSearchResult> {
  const { roleSessionKey: key } = user;
  return axios
    .post(getGeneralMessageRecipientsUrl(user, recipientType, 'search'), filter, {
      headers: { usid: key }
    })
    .then((response: { data: GeneralMessageRecipientSearchResult }) => response.data);
}

export function getGeneralMessageSelectedRecipientsPreview(
  user: AppUser,
  recipientType: GeneralMessageRecipientType,
  params: GeneralMessageSelectedRecipientsParams
): Promise<GeneralMessageRecipientPreviewItem[]> {
  const { roleSessionKey: key } = user;
  const config = {
    headers: { usid: key }
  };

  return axios
    .post(getGeneralMessageRecipientsUrl(user, recipientType, 'selected-preview'), params, config)
    .then((response: { data: GeneralMessageRecipientPreviewItem[] }) => {
      return response.data;
    });
}

export function getGeneralMessageSelectedRecipientsCount(
  user: AppUser,
  recipientType: GeneralMessageRecipientType,
  params: GeneralMessageSelectedRecipientsParams
): Promise<Count> {
  const { roleSessionKey: key } = user;
  const config = {
    headers: { usid: key }
  };

  return axios
    .post(getGeneralMessageRecipientsUrl(user, recipientType, 'selected-count'), params, config)
    .then((response: { data: Count }) => {
      return response.data;
    });
}

export function searchGeneralMessageSelectedRecipients(
  user: AppUser,
  recipientType: GeneralMessageRecipientType,
  params: GeneralMessageSelectedRecipientsParams
): Promise<GeneralMessageRecipientSearchResult> {
  const { roleSessionKey: key } = user;
  return axios
    .post(getGeneralMessageRecipientsUrl(user, recipientType, 'selected-search'), params, { headers: { usid: key } })
    .then((response: { data: GeneralMessageRecipientSearchResult }) => response.data);
}

export function resolveGeneralMessageRecipients(
  user: AppUser,
  params: GeneralMessageRecipientsResolveParams
): Promise<GeneralMessageRecipientsResolveResult> {
  const { roleSessionKey: key } = user;
  const config = {
    headers: { usid: key }
  };

  return axios
    .post(getGeneralMessageRecipientsUrl(user, undefined, 'resolve'), params, config)
    .then((response: { data: GeneralMessageRecipientsResolveResult }) => {
      return response.data;
    });
}

/** GENERAL MESSAGE TABLE */
export function createSchoolGeneralMessage(user: AppUser, data) {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = {
    headers: { usid: key, 'notification-mode': 'MANUAL' }
  };

  return axios.post(`${window.apiBase}/i/schools/${activeSchoolId}/messages/general`, data, config).then(response => {
    return response.data;
  });
}

export function updateSchoolGeneralMessage(user: AppUser, data, messageId) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key, 'notification-mode': 'MANUAL' }
  };

  return axios
    .put(`${window.apiBase}/i/schools/${activeSchoolId}/messages/general/${messageId}`, data, config)
    .then(response => {
      return response.data;
    });
}

export function sendSchoolGeneralMessage(user: AppUser, generalMessageId) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/messages/general/${generalMessageId}/send`, {}, config)
    .then(response => {
      return response.data;
    });
}

export function scheduleSchoolGeneralMessage(user: AppUser, generalMessageId: string, scheduleAt: string) {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = {
    headers: { usid: key }
  };

  return axios
    .put(
      `${window.apiBase}/i/schools/${activeSchoolId}/messages/general/${generalMessageId}/schedule`,
      { scheduleAt: scheduleAt },
      config
    )
    .then(response => {
      return response.data;
    });
}

export function returnSchoolGeneralMessageToDraft(user: AppUser, generalMessageId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = {
    headers: { usid: key }
  };

  return axios
    .delete(`${window.apiBase}/i/schools/${activeSchoolId}/messages/general/${generalMessageId}/schedule`, config)
    .then(response => {
      return response.data;
    });
}

export function getGeneralMessageActionDescriptor(user: AppUser, generalMessageId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = {
    headers: { usid: key }
  };

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/messages/general/${generalMessageId}/schedule`, config)
    .then(response => {
      return response.data;
    });
}

export function getSchoolGeneralMessage(user: AppUser, generalMessageId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/messages/general/${generalMessageId}`, config)
    .then(response => {
      return response.data;
    });
}

export function getSchoolGeneralMessageDetails(user: AppUser, generalMessageId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = {
    headers: { usid: key }
  };

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/messages/general/${generalMessageId}/details`, config)
    .then(response => response.data)
    .catch(error => {
      // Keep mixed frontend/backend deployments usable. Older backend builds do
      // not have the lightweight details route yet, while the legacy route is
      // still compatible with the drawer response shape.
      if (error && error.response && error.response.status === 404) {
        return getSchoolGeneralMessage(user, generalMessageId);
      }
      throw error;
    });
}

export function getSchoolGeneralMessageForEditor(user: AppUser, generalMessageId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = {
    headers: { usid: key }
  };

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/messages/general/${generalMessageId}/editor`, config)
    .then(response => response.data);
}

export function getSchoolGeneralMessages(user: AppUser, filter = DEFAULT_FILTER) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter
    }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/messages/general`, config).then(response => {
    return response.data;
  });
}

export function getAllSchoolGeneralMessages(user: AppUser, where?) {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...where
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  }

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/messages/general`, config).then(response => {
    return response.data;
  });
}

export function getSchoolGeneralMessagesCount(user: AppUser, where?): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: where
        }
      }
    };
  } else {
    config = {
      headers: { usid: key }
    };
  }

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/messages/general/count`, config)
    .then((response: { data: Count }) => {
      return response.data;
    });
}

export interface GeneralMessageStatusCounts {
  SENT: number;
  SCHEDULED: number;
  DRAFT: number;
}

export interface GeneralMessageDeliverySummary {
  messageId: string;
  statistics: any;
}

export function getSchoolGeneralMessageStatusCounts(user: AppUser, where?): Promise<GeneralMessageStatusCounts> {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = {
    headers: { usid: key },
    ...(typeof where !== 'undefined' ? { params: { filter: { where } } } : {})
  };

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/messages/general/status-counts`, config)
    .then((response: { data: GeneralMessageStatusCounts }) => response.data);
}

export function getGeneralMessageRecipientPlanPreview(
  user: AppUser,
  generalMessageId: string
): Promise<GeneralMessageRecipientPlanPreview> {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = {
    headers: { usid: key }
  };

  return axios
    .get(
      `${window.apiBase}/i/schools/${activeSchoolId}/messages/general/${generalMessageId}/recipients/preview`,
      config
    )
    .then(response => response.data as GeneralMessageRecipientPlanPreview);
}

export function getSchoolGeneralMessageSummaries(
  user: AppUser,
  messageIds: string[]
): Promise<GeneralMessageDeliverySummary[]> {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = { headers: { usid: key } };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/messages/general/summaries`, { messageIds }, config)
    .then((response: { data: GeneralMessageDeliverySummary[] }) => response.data);
}

export function getGeneralMessageNotifications(user: AppUser, messageId: string, filter = DEFAULT_FILTER) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter
    }
  };

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/messages/general/${messageId}/notifications`, config)
    .then(response => {
      return response.data;
    });
}

export function getGeneralMessageNotificationsCount(user: AppUser, messageId: string, where?): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: where
        }
      }
    };
  } else {
    config = {
      headers: { usid: key }
    };
  }

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/messages/general/${messageId}/notifications/count`, config)
    .then((response: { data: Count }) => {
      return response.data;
    });
}

export function getAllGeneralMessageNotifications(user: AppUser, messageId: string, where?) {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: where,
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  }

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/messages/general/${messageId}/notifications`, config)
    .then(response => {
      return response.data;
    });
}

export interface GeneralMessageRetrySummary {
  statuses: { [status: string]: number };
  retryAvailable: number;
  unavailableRetryAvailable?: number;
}

export function getGeneralMessageRetrySummary(user: AppUser, messageId: string): Promise<GeneralMessageRetrySummary> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/messages/general/${messageId}/notifications/retries`, {
      headers: { usid: key }
    })
    .then(response => response.data as GeneralMessageRetrySummary);
}

export function retryFailedGeneralMessageDeliveries(user: AppUser, messageId: string): Promise<{ queued: number }> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/messages/general/${messageId}/notifications/retries`,
      {},
      { headers: { usid: key } }
    )
    .then(response => response.data as { queued: number });
}

export function deleteSchoolGeneralMessage(user: AppUser, generalMessageId) {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = {
    headers: { usid: key }
  };

  return axios
    .delete(`${window.apiBase}/i/schools/${activeSchoolId}/messages/general/${generalMessageId}`, config)
    .then(response => {
      return response.data;
    });
}
